import { CircularProgress } from '@material-ui/core';
import { modeUIContext, setIsEditingForm } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { BaseInfoCompProps, capitalizeAll } from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { FileRequest, FileRequestAction, isDownloadFileRequest, isFileRequest, isUploadFileRequest } from '@moderepo/mode-apis';
import { fetchFileRequestById, fetchRobotById, rcDataContext, selectFileRequestById, selectRobotById } from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../../utils/customHooks';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { RCFileRequestInfo, RCFileRequestInfoProps } from '../../../components/robot-cloud/file-transfer/RCFileRequestInfo';
import { getDateTimeAndElapsedTime, getDateTimeDisplay, getMemorySizeDisplayValue } from '../../containerUtils';
import { getFileRequestActionName, getFileRequestStatusName, getFileRequestTargetName, getSupportedFileType, useGetRCGatewayConfig }
    from '../RCUtils';
import { RCCreateFileRequestContainer } from './RCCreateFileRequestContainer';



/**
 * Check if it will be a DOWNLOAD or UPLOAD request and whether it is a request for DEVICE or ROBOT.
 * We need to do this check in a few places therefore we do it here once, cache it and use it later.
 * If this component is used for CREATE a new request then we check the props.selectedFileRequest. If this component is used for
 * displaying an existing request then we check the selectedFileRequest.action
 * This function will return an Object with 4 flags, isDownloadRequest, isUploadRequest, isDeviceRequest, and isRobotRequest
 */
const getFileRequestTypes = (selectedRequest: FileRequest | FileRequestAction | undefined): {
    readonly isDownloadRequest: Boolean;
    readonly isUploadRequest: Boolean;
    readonly isDeviceRequest: Boolean;
    readonly isRobotRequest: Boolean;
} => {

    // We have not loaded the request data yet so we don't know
    if (selectedRequest === undefined) {
        return {
            isDownloadRequest: false,
            isUploadRequest  : false,
            isDeviceRequest  : false,
            isRobotRequest   : false,
        };
    }


    // selectedRequest is a FileRequest object so we need to check the selectedRequest.action
    if (isFileRequest(selectedRequest)) {
        if (isDownloadFileRequest(selectedRequest)) {
            return {
                isDownloadRequest: true,
                isUploadRequest  : false,
                isDeviceRequest  : selectedRequest.action === FileRequestAction.DOWNLOAD_FROM_DEVICE
                                     || selectedRequest.action === FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER,
                isRobotRequest: selectedRequest.action === FileRequestAction.DOWNLOAD_FROM_ROBOT,
            };
        }
        return {
            isDownloadRequest: false,
            isUploadRequest  : true,
            isDeviceRequest  : selectedRequest.action === FileRequestAction.UPLOAD_TO_DEVICE
                                || selectedRequest.action === FileRequestAction.UPLOAD_TO_CONTROL_SERVER,
            isRobotRequest: selectedRequest.action === FileRequestAction.UPLOAD_TO_ROBOT,
        };
    }


    // The selectedRequest must be one of the FileRequestAction types
    if (
        selectedRequest === FileRequestAction.DOWNLOAD_FROM_DEVICE
        || selectedRequest === FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER
        || selectedRequest === FileRequestAction.DOWNLOAD_FROM_ROBOT
    ) {
        return {
            isDownloadRequest: true,
            isUploadRequest  : false,
            isDeviceRequest  : selectedRequest === FileRequestAction.DOWNLOAD_FROM_DEVICE
                || selectedRequest === FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER,
            isRobotRequest: selectedRequest === FileRequestAction.DOWNLOAD_FROM_ROBOT,
        };
    }
    return {
        isDownloadRequest: false,
        isUploadRequest  : true,
        isDeviceRequest  : selectedRequest === FileRequestAction.UPLOAD_TO_DEVICE
                            || selectedRequest === FileRequestAction.UPLOAD_TO_CONTROL_SERVER,
        isRobotRequest: selectedRequest === FileRequestAction.UPLOAD_TO_ROBOT,
    };
};



interface RCFileRequestInfoContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly homeId: number;
    readonly deviceId: number;
    readonly robotId?: string | undefined;

    // This can be either a number or a FileRequestAction. To show a preview of an existing file request, pass the fileRequest's ID to this
    // prop. To create a new file request, pass a FileRequestAction to this prop.
    readonly selectedFileRequest: number | FileRequestAction;

    // This container used FileRequestInfo component to display file request info and if the component has its own customizable props which can be
    // set through this props.
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
        readonly formProps?: BaseInfoCompProps['formProps'] | undefined;
    } | undefined;

    readonly onCancel: ()=> void;
    readonly onFileRequestCreated: ()=> void;
}


/**
 * This container is for displaying a FileRequest and provide all the logic for a FileRequestInfoComp e.g. fetching data and updating data.
 */
export const RCFileRequestInfoContainer: React.FC<RCFileRequestInfoContainerProps> = (props: RCFileRequestInfoContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);

    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    // Get the RC Gateway config for the selected device. We will need to get FileTransfer settings from this config.
    const rcGatewayConfig = useGetRCGatewayConfig(props.deviceId);

    // Get the file request object from the data state if the selectedFileRequest is an ID
    const selectedFileRequest = typeof props.selectedFileRequest === 'number'
        ? selectFileRequestById(rcDataState, props.homeId, props.selectedFileRequest) : undefined;

    // If selectedFileRequest is NOT a number then we are in a Create File Request mode
    const isNewFileRequest = typeof props.selectedFileRequest !== 'number';

    // if selectedFileRequest is NOT a number, we can assume it is one of the FileRequestAction type.
    const newFileRequestActionType = typeof props.selectedFileRequest !== 'number' ? props.selectedFileRequest as FileRequestAction : undefined;


    const { isDownloadRequest, isRobotRequest } = useMemo(() => {
        return getFileRequestTypes(typeof props.selectedFileRequest === 'number' ? selectedFileRequest : props.selectedFileRequest);
    }, [props.selectedFileRequest, selectedFileRequest]);


    // If the file request is for a robot, get the Robot info for the target robot
    const selectedRobot = selectedFileRequest && selectedFileRequest.robotId !== undefined && isRobotRequest
        ? selectRobotById(rcDataState, selectedFileRequest.homeId, selectedFileRequest.robotId)
        : undefined;


    // This useEffect is used for loading file request data for the selected file request if we don't have the data yet
    useEffect(() => {
        if (typeof props.selectedFileRequest === 'number' && !selectedFileRequest) {
            rcDataDispatch(fetchFileRequestById(props.homeId, props.selectedFileRequest)).catch(() => {
                // ignore error because dataDispatch already handled it
            });
        }
    }, [props.selectedFileRequest, selectedFileRequest, rcDataDispatch, props.homeId]);


    /**
     * This useEffect is used for fetching Robot info if the file request is for a robot
     */
    useEffect(() => {
        if (selectedFileRequest && selectedFileRequest.robotId && isRobotRequest && !selectedRobot) {
            rcDataDispatch(fetchRobotById(props.projectId, selectedFileRequest.homeId, selectedFileRequest.robotId)).catch(() => {
                // ignore error
            });
        }
    }, [isRobotRequest, props.projectId, rcDataDispatch, selectedFileRequest, selectedRobot]);



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * Automatically set editing mode to true on component init if props.homeId is undefined which is when user want to create a Home
     */
    useEffect(() => {
        if (isNewFileRequest) {
            onChangeEditingMode(true);
        }
    }, [isNewFileRequest, onChangeEditingMode]);



    /**
     * Create the fields settings for the FileRequestInfoComp and cache it
     */
    const fileRequestInfoCompFieldsSettings = useMemo((): RCFileRequestInfoProps['fields'] | undefined => {


        if (selectedFileRequest) {
            const supportedFileTypes = getSupportedFileType(rcGatewayConfig, selectedFileRequest.action, project, loggedInAgent);

            return {
                id: {
                    label       : capitalizeAll(trans('label.id')),
                    displayValue: selectedFileRequest.id.toString(),
                },
                status: {
                    label       : capitalizeAll(trans('label.status')),
                    displayValue: capitalizeAll(trans(getFileRequestStatusName(selectedFileRequest.status))),
                },
                homeId: {
                    label       : capitalizeAll(trans('label.home_id')),
                    displayValue: selectedFileRequest.homeId.toString(),
                },
                deviceId: {
                    label       : capitalizeAll(trans('label.device_id')),
                    displayValue: selectedFileRequest.deviceId.toString(),
                },
                robotId: isDownloadRequest && isRobotRequest
                    ? {
                        label       : capitalizeAll(trans('label.robot_id')),
                        displayValue: selectedFileRequest.robotId,
                    } : undefined,
                robotClass: isRobotRequest
                    ? {
                        label       : capitalizeAll(trans('label.robot_class')),
                        displayValue: selectedRobot?.robotClass,
                    } : undefined,
                action: {
                    label       : capitalizeAll(trans('file_transfer.label.action')),
                    displayValue: capitalizeAll(trans(getFileRequestActionName(selectedFileRequest.action))),
                },
                errorMessage: selectedFileRequest.errorMessage
                    ? {
                        label       : capitalizeAll(trans('file_transfer.label.error_message')),
                        displayValue: selectedFileRequest.errorMessage || capitalizeAll(trans('label.none')),
                    } : undefined,
                target: {
                    label       : capitalizeAll(trans('file_transfer.label.target')),
                    displayValue: capitalizeAll(trans(getFileRequestTargetName(selectedFileRequest.target))),
                },
                createdAt: {
                    label       : capitalizeAll(trans('label.date_created')),
                    displayValue: getDateTimeAndElapsedTime(selectedFileRequest.createdAt, trans, true),
                },
                updatedAt: {
                    label       : capitalizeAll(trans('label.date_updated')),
                    displayValue: getDateTimeAndElapsedTime(selectedFileRequest.updatedAt, trans, true),
                },
                downloadRequestParameters: isDownloadFileRequest(selectedFileRequest)
                    ? {
                        fileType: {
                            label       : capitalizeAll(trans('file_transfer.input.label.file_type')),
                            displayValue: capitalizeAll(trans(selectedFileRequest.parameters.fileType)),

                            // If this file request type support EXACTLY 1 file type option, we know the user didn't have to select any option
                            // because we automatically select that options default therefore there is no need to show what file type was selected.
                            hidden: supportedFileTypes?.length === 1,
                        },
                        begin: {
                            label       : capitalizeAll(trans('label.start_date')),
                            displayValue: getDateTimeDisplay(selectedFileRequest.parameters.fetchFileParameters.begin, trans, true),
                        },
                        end: {
                            label       : capitalizeAll(trans('label.end_date')),
                            displayValue: getDateTimeDisplay(selectedFileRequest.parameters.fetchFileParameters.end, trans, true),
                        },
                    }
                    : undefined,
                uploadRequestParameters: isUploadFileRequest(selectedFileRequest)
                    ? {
                        fileName: {
                            label       : capitalizeAll(trans('file_transfer.input.label.file_name')),
                            displayValue: selectedFileRequest.parameters.fileName,
                        },
                        fileSetSize: {
                            label       : capitalizeAll(trans('file_transfer.input.label.file_size')),
                            displayValue: getMemorySizeDisplayValue(selectedFileRequest.parameters.fileSetSize, trans),
                        },
                        fileSetChecksum: {
                            label       : capitalizeAll(trans('file_transfer.input.label.checksum')),
                            displayValue: selectedFileRequest.parameters.fileSetChecksum,
                        },
                    }
                    : undefined,
                downloadRequestResults: isDownloadFileRequest(selectedFileRequest) && selectedFileRequest.results
                    ? {
                        fileSetSize: {
                            label       : capitalizeAll(trans('file_transfer.input.label.file_size')),
                            displayValue: getMemorySizeDisplayValue(selectedFileRequest.results.fileSetSize, trans),
                        },
                        fileSetChecksum: {
                            label       : capitalizeAll(trans('file_transfer.input.label.checksum')),
                            displayValue: selectedFileRequest.results.fileSetChecksum,
                        },
                    }
                    : undefined,
            };
        }

        return undefined;
    }, [isDownloadRequest, isRobotRequest, loggedInAgent, project, rcGatewayConfig, selectedFileRequest, selectedRobot, trans]);


    return (
        <>
            {project && !isNewFileRequest && fileRequestInfoCompFieldsSettings && (
                <RCFileRequestInfo
                    className={clsx(props.className, props.infoCompProps?.className, isEditing && 'editing')}
                    formProps={props.infoCompProps?.formProps}
                    isEditing={isEditing}
                    title={capitalizeAll(trans('file_transfer.panel.title.file_request_info'))}
                    icon={isDownloadRequest ? 'fas fa-file-download' : 'fas fa-file-upload'}
                    fields={fileRequestInfoCompFieldsSettings}
                    texts={{
                        requestMetadataTitle: capitalizeAll(trans('file_transfer.input.label.request_parameters')),
                        requestResultsTitle : capitalizeAll(trans('file_transfer.input.label.results')),
                    }}
                />
            )}

            {project && isNewFileRequest && newFileRequestActionType && (
                <RCCreateFileRequestContainer
                    className={clsx(props.className, props.infoCompProps?.className, isEditing && 'editing')}
                    projectId={props.projectId}
                    homeId={props.homeId}
                    deviceId={props.deviceId}
                    robotId={props.robotId}
                    fileRequestActionType={newFileRequestActionType}
                    onCancel={props.onCancel}
                    onFileRequestCreated={props.onFileRequestCreated}
                />
            )}

            {(!project || (!isNewFileRequest && !selectedFileRequest)) && (
                <CircularProgress />
            )}
        </>

    );
};



// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('RCFileRequestInfoContainer', RCFileRequestInfoContainer);
